import React, { FunctionComponent, ReactNode, useContext, useMemo } from 'react';

import { TooltipStateful } from '@yandex-360/orb-components/dist/components/Tooltip';
import cn from 'classnames';
import isNil from 'lodash/isNil';

import { Button } from '../../../../../../shared/components';
import { ButtonProps } from '../../../../../../shared/components/button';
import SvgImage from '../../../../../../shared/components/svg-image';
import { ImageName } from '../../../../../../shared/components/svg-image/images';
import { ReleasePhase } from '../../../../../../types';
import { CollapseBreakpoint } from '../../constants';
import { ToolbarContext } from '../../index';
import classes from './style.css';

interface WrapperProps {
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

export interface Props {
    iconName?: ImageName;
    icon?: ReactNode;
    onClick?: () => void;
    title: string;
    disabled?: boolean;
    className?: string | null;
    classNameWrapper?: string;
    buttonProps?: Partial<ButtonProps>;
    content?: ReactNode;
    dotType?: 'red' | 'green';
    collapseBreakpoint?: CollapseBreakpoint | null;
    wrapperProps?: WrapperProps;
    releasePhase?: ReleasePhase | null;
    tooltip?: string | null;
    testId?: string;
}

const RecordingDot = () => <div className={cn(classes.statusDot, classes.red)} />;

const GreenDot = () => <div className={cn(classes.statusDot, classes.green)} />;

const ToolbarButton: FunctionComponent<Props> = ({
    onClick,
    iconName,
    icon,
    disabled,
    title,
    className,
    classNameWrapper,
    buttonProps,
    content,
    dotType,
    collapseBreakpoint = CollapseBreakpoint.SMALL,
    wrapperProps,
    releasePhase,
    tooltip,
    testId,
}) => {
    const buttonIcon = useMemo(() => {
        if (icon) {
            return icon;
        }

        if (iconName) {
            return <SvgImage imageName={iconName} />;
        }

        return undefined;
    }, [iconName, icon]) as ButtonProps['iconLeft'];

    const { buttonSize } = useContext(ToolbarContext);

    const hasContent = !isNil(content) && content !== false;
    const isCollapsible = Boolean(collapseBreakpoint && hasContent);
    const collapseBreakpointClass = isCollapsible && collapseBreakpoint ? classes[collapseBreakpoint] : undefined;

    const buttonClasses = cn(
        className,
        {
            [classes.collapsible]: isCollapsible,
        },
        collapseBreakpointClass
    );

    const collapsibleContent = useMemo(() => {
        return isCollapsible && content ? <div className={classes.content}>{content}</div> : null;
    }, [content, isCollapsible]);

    const buttonInnerContent = collapsibleContent || content;

    const buttonContent = (
        <>
            {hasContent ? (
                <Button
                    view="neutral"
                    size={buttonSize}
                    iconLeft={buttonIcon}
                    onClick={onClick}
                    className={buttonClasses}
                    isDisabled={disabled}
                    testId={testId}
                    title={title}
                    {...{ 'aria-label': title }}
                    {...buttonProps}
                >
                    {buttonInnerContent}
                </Button>
            ) : (
                <Button
                    view="neutral"
                    size={buttonSize}
                    icon={buttonIcon}
                    onClick={onClick}
                    className={buttonClasses}
                    isDisabled={disabled}
                    testId={testId}
                    title={title}
                    {...{ 'aria-label': title }}
                    {...buttonProps}
                />
            )}
        </>
    );

    return (
        <div className={cn(classes.container, classNameWrapper)} {...wrapperProps}>
            {tooltip ? (
                <TooltipStateful content={tooltip} trigger="hover">
                    <div>{buttonContent}</div>
                </TooltipStateful>
            ) : (
                buttonContent
            )}
            {releasePhase && (
                <div className={classes.releasePhaseIconContainer}>
                    <SvgImage imageName={releasePhase} />
                </div>
            )}
            {dotType && (
                <div className={classes.dotContainer}>
                    {dotType === 'green' && <GreenDot />}
                    {dotType === 'red' && <RecordingDot />}
                </div>
            )}
        </div>
    );
};
ToolbarButton.displayName = 'ToolbarButton';

export { ToolbarButton };
